import React, { memo, useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { User, Mail, Check, AlertCircle, Key, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { portalAPI } from '../../api';
import { format } from 'date-fns';

const PortalAccessDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  contact
}) => {
  const [portalPassword, setPortalPassword] = useState('');
  const [portalCredentials, setPortalCredentials] = useState(null);
  const [existingPortalAccess, setExistingPortalAccess] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  const checkExistingAccess = useCallback(async () => {
    setPortalLoading(true);
    try {
      const res = await portalAPI.checkAccess(contactId);
      if (res.data.has_access) {
        setExistingPortalAccess(res.data);
      } else {
        setExistingPortalAccess(null);
      }
    } catch (error) {
      setExistingPortalAccess(null);
    } finally {
      setPortalLoading(false);
    }
  }, [contactId]);

  // Check for existing portal access when dialog opens
  useEffect(() => {
    if (open && contactId) {
      checkExistingAccess();
    }
  }, [open, contactId, checkExistingAccess]);

  const handleClose = useCallback(() => {
    setPortalPassword('');
    setPortalCredentials(null);
    setExistingPortalAccess(null);
    setShowResetPassword(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleCreatePortalAccess = useCallback(async () => {
    if (!portalPassword || portalPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      const res = await portalAPI.createAccess({
        contact_id: contactId,
        email: contact?.email,
        password: portalPassword
      });
      setPortalCredentials(res.data);
      toast.success('Portal access created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create portal access');
    }
  }, [portalPassword, contactId, contact]);

  const handleResetPortalPassword = useCallback(async () => {
    if (!portalPassword || portalPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      const res = await portalAPI.adminResetPassword({
        contact_id: contactId,
        new_password: portalPassword
      });
      setPortalCredentials(res.data);
      setShowResetPassword(false);
      toast.success('Portal password reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  }, [portalPassword, contactId]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {existingPortalAccess ? 'Portal Access Management' : 'Invite to Customer Portal'}
          </DialogTitle>
          <DialogDescription>
            {existingPortalAccess 
              ? `Manage portal access for ${contact?.first_name} ${contact?.last_name}`
              : `Create portal access for ${contact?.first_name} ${contact?.last_name} to view their tickets, payments, and warranties.`
            }
          </DialogDescription>
        </DialogHeader>
        
        {portalLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : portalCredentials ? (
          <div className="space-y-4">
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check className="h-5 w-5 text-emerald-600" />
                </div>
                <span className="font-medium text-emerald-700">
                  {showResetPassword ? 'Password Reset Successful!' : 'Portal Access Created!'}
                </span>
              </div>
              <p className="text-sm text-emerald-700 mb-3">
                Share these credentials with the customer:
              </p>
              <div className="space-y-2 bg-white p-3 rounded border">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Portal URL:</span>
                  <code className="text-xs bg-slate-100 px-2 py-1 rounded">/customer-login</code>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Email:</span>
                  <span className="font-medium">{portalCredentials.email || contact?.email}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Password:</span>
                  <span className="font-medium font-mono bg-emerald-100 px-2 py-0.5 rounded">{portalCredentials.temporary_password}</span>
                </div>
              </div>
            </div>
            
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              Email invite is currently SIMULATED. Copy credentials manually for now.
            </p>
            
            <DialogFooter>
              <Button onClick={handleClose}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : existingPortalAccess ? (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <Check className="h-5 w-5 text-blue-600" />
                </div>
                <span className="font-medium text-blue-700">Portal Access Active</span>
              </div>
              <div className="space-y-1 text-sm">
                <p><span className="text-muted-foreground">Email:</span> {existingPortalAccess.email}</p>
                <p><span className="text-muted-foreground">Status:</span> {existingPortalAccess.is_active ? 'Active' : 'Inactive'}</p>
                {existingPortalAccess.last_login && (
                  <p><span className="text-muted-foreground">Last Login:</span> {format(new Date(existingPortalAccess.last_login), 'PPp')}</p>
                )}
                {existingPortalAccess.created_at && (
                  <p><span className="text-muted-foreground">Created:</span> {format(new Date(existingPortalAccess.created_at), 'PPP')}</p>
                )}
              </div>
            </div>
            
            {showResetPassword ? (
              <div className="space-y-3">
                <Label>New Password *</Label>
                <Input
                  type="password"
                  value={portalPassword}
                  onChange={(e) => setPortalPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  data-testid="portal-reset-password-input"
                />
                <p className="text-xs text-muted-foreground">
                  Enter a new password for this customer&apos;s portal access
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowResetPassword(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleResetPortalPassword} data-testid="confirm-reset-password-btn">
                    Reset Password
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  If the customer forgot their password, you can reset it here:
                </p>
                <Button 
                  variant="outline" 
                  onClick={() => setShowResetPassword(true)}
                  className="w-full"
                  data-testid="reset-portal-password-btn"
                >
                  <Key className="h-4 w-4 mr-2" /> Reset Portal Password
                </Button>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Portal will be sent to:</p>
                  <p className="text-blue-700">{contact?.email}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Set Temporary Password *</Label>
              <Input
                type="password"
                value={portalPassword}
                onChange={(e) => setPortalPassword(e.target.value)}
                placeholder="Min 6 characters"
                data-testid="portal-password-input"
              />
              <p className="text-xs text-muted-foreground">
                Customer can change this after first login
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleCreatePortalAccess} data-testid="create-portal-access-btn">
                Create Access & Send Invite
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
});

PortalAccessDialog.displayName = 'PortalAccessDialog';

export default PortalAccessDialog;
